import { Subscription } from 'rxjs';
import { Component, OnInit, OnDestroy } from '@angular/core';

import {
  EntityValidationErrorResponse,
  SelectableTransaction, Transaction,
  Entity, GenerateReportPayload,
  FinancialDataValueEnteredInEnum,
  Container,
  LocalFileCreateModalPayload,
  ReportGeneratorTemplate,
  Currency,
  Country,
  LanguageIsoInfo,
} from '../../../shared/models';
import { PaginationConstants, TinyTextEditor } from '../../../shared/constants';

import { ModalService } from '../../../shared/modules/modal/modal.service';
import {
  NotificationService,
  UtilsService,
  EntitiesService,
  TpReportGeneratorService,
  SessionService,
  CompSearchService,
  CurrenciesService,
  CountriesService,
  ReportRepositoryService
} from '../../../shared/services';
import { AllowedLimitWordingRoles } from '../../../shared/models/user-permissions-policy';

import {
  ClosableModalWithErrorResponseModel
} from '../../../shared/components/base/closable-modal-with-error-response-model';

import { TranslationConstants } from '../../../shared/constants/translation';
import { ObjectResponse } from '../../../shared/interfaces';
import { InputCurrencyOption } from '../../../shared/models/currency';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'local-report-modal',
  templateUrl: './create-local-file-report-modal.component.html',
})
export class CreateLocalFileReportModalComponent
  extends ClosableModalWithErrorResponseModel<EntityValidationErrorResponse>
  implements OnInit, OnDestroy {

  data: LocalFileCreateModalPayload;
  subscriptions: Subscription[] = [];
  reportTitle: string;
  tradingPartnerId: number;
  potentialTradingPartners: Entity[];
  selectableTransactions: SelectableTransaction[] = [];
  viewedTransactions: SelectableTransaction[] = [];
  selectedStudyId: number = null;
  currencyOptionId: number;
  templateId: string;
  language: string;
  languages: LanguageIsoInfo[] = [];

  inputCurrencyOptions: InputCurrencyOption[] = [];
  pageSizes: number[];
  workingContainer: Container;

  totalItems: number;
  selectedPageSize = PaginationConstants.DEFAULT_PAGE_SIZE;

  generatingReport: boolean = false;
  generatedReport: boolean = false;

  executiveSummaryIntro: string | null = null;
  executiveSummaryConclusion: string | null = null;

  templates: ReportGeneratorTemplate[];
  mneCurrency: Currency;
  upe: Entity;
  taxJurisdictionCurrency: Currency;
  primaryEntityList: Entity[];
  reportStage: string = 'Draft';
  previousSelectedTradingPartnerIds: number[] = [];
  countryId: number;
  selectedTradingPartnerIds: number[] = [];
  selectedPrimaryEntityIds: number[] = [];
  tradingPartnerEntities: { [key: number]: Entity } = {};
  primaryTradingPartnerMap: { [key: number]: number[] } = {};
  primaryTradingPartnerTransactionMap: Record<string, SelectableTransaction[]> = {};
  domicile: string;
  firstPrimaryEntity: Entity;
  shouldDisplayPageNumber: boolean = true;
  shouldLimitWording: boolean;
  tinyInit = TinyTextEditor.INIT;
  userRole: string;
  isAllowedLimitWording: boolean = false;

  default2019Template = 'TPIQ-Report-Template-2019';
  default2018Template = 'Main-Report-Template';

  constructor(
    protected notificationService: NotificationService,
    protected modalService: ModalService,
    private _utilsService: UtilsService,
    private _entitiesService: EntitiesService,
    private _sessionService: SessionService,
    private _tpReportGeneratorService: TpReportGeneratorService,
    private _compSearchService: CompSearchService,
    private _currenciesService: CurrenciesService,
    private _countriesService: CountriesService,
    private _translateService: TranslateService,
    private _reportService: ReportRepositoryService
  ) {
    super(notificationService, modalService);
    this.validationErrors = new EntityValidationErrorResponse();
    this.workingContainer = this._sessionService.getWorkingContainer();
  }

  ngOnInit(): void {
    this.userRole = this._sessionService.getUserRole();
    this.isAllowedLimitWording = AllowedLimitWordingRoles.includes(this.userRole);
    this.shouldLimitWording = this.isAllowedLimitWording;
    this.listenForErrorMessages();
    this.pageSizes = PaginationConstants.PAGE_SIZES;
    this.primaryEntityList = this.data.primaryEntityList;
    this.firstPrimaryEntity = this.primaryEntityList[0];
    this.countryId = this.firstPrimaryEntity.domicile.countryId;
    this.domicile = this.firstPrimaryEntity.domicile.name;
    this.getPrimaryEntityCurrency();
    this.getTemplatesForEntity(this.firstPrimaryEntity.entityId);
    this.getLanguages(this.countryId, this.workingContainer.taxYear);
    this.getUpe();
    this.constructPrimaryTradingTransactionMaps();
    this.initEntityData();
    if (this.data.report) {
      this.reportTitle = this.data.report.name;
    }
    this.getLatestGeneratedVersion();
    this.language = 'en';
  }

  getLatestGeneratedVersion(): void {
    if (this.data.report) {
      this.subscriptions.push(this._reportService.getReportInstance(this.data.report.reportId, this.data.reportInstanceId).subscribe((reportInstance) => {
        this.data.latestGeneratedVersion = reportInstance;
        this.executiveSummaryConclusion = this.data.latestGeneratedVersion.executiveSummaryConclusion;
        this.executiveSummaryIntro = this.data.latestGeneratedVersion.executiveSummaryIntro;
        this.shouldDisplayPageNumber = this.data.latestGeneratedVersion.shouldDisplayPageNumber;
        this.initPreSelectedTransactions();
      }));
    }
  }

  initPreSelectedTransactions(): void {
    this.selectedPrimaryEntityIds = this.data.latestGeneratedVersion.primaryEntities.map(primaryEntity => primaryEntity.entityId);
    this.getPotentialTradingPartners();
    this.selectedTradingPartnerIds = this.data.latestGeneratedVersion.tradingPartners.map(tradingPartner => tradingPartner.entityId);
    this.tradingPartnersChange();
    this.data.latestGeneratedVersion.reportTransactions.forEach(reportTransaction => {
      this.selectableTransactions.forEach(transaction => {
        if (transaction.transactionId === reportTransaction.transactionId) {
          transaction.selected = true;
        }
      });
    });
    this.getCurrencyOptions();
  }

  initEntityData(): void {
    this.initTransactions();
    this.getCurrencyOptions();
    Object.keys(this.primaryTradingPartnerTransactionMap).forEach(
      objKey => {
        this.primaryTradingPartnerTransactionMap[objKey].forEach(
          transaction => { transaction.selected = false; }
        );
      }
    );
  }

  initTransactions(): void {
    this.selectableTransactions = [];
    this.viewedTransactions = [];
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => {
      sub.unsubscribe();
    });
  }

  exitModal(): void {
    this.closeAllModals();
  }

  getCurrencyOptions(): void {
    let hasLegalEntityCurrencyConversion =
      this.selectedPrimaryEntityIds.some(selectedPrimaryEntityId => {
        return this.primaryEntityList.some(primaryEntity => {
          return !!(primaryEntity.entityId === selectedPrimaryEntityId && primaryEntity.legalEntityCurrencyConversion && primaryEntity.legalEntityCurrencyConversion.length);
        });
      });

    if (this.data && hasLegalEntityCurrencyConversion) {
      this.inputCurrencyOptions = this._utilsService.enumToArray(FinancialDataValueEnteredInEnum);
    } else {
      this.inputCurrencyOptions = [{
        id: 1,
        name: FinancialDataValueEnteredInEnum[1],
      }];
      this.currencyOptionId = this.inputCurrencyOptions[0].id;
    }
  }

  generateInternalLocalfileReport(): void {
    const selectedTransactions = this.selectableTransactions.filter(transaction => transaction.selected);
    const selectedTransactionIds = selectedTransactions.map(transaction => transaction.transactionId);
    let currencyId;

    if (this.currencyOptionId === 1) {
      currencyId = this.mneCurrency.currencyId;
    } else if (this.currencyOptionId === 2) {
      currencyId = this.taxJurisdictionCurrency.currencyId;
    }

    const payload: GenerateReportPayload = {
      reportStage: this.reportStage,
      currencyId,
      selectedTransactionIds,
      name: this.reportTitle,
      templateId: this.templateId,
      containerId: this.workingContainer.containerId,
      primaryEntityIds: this.selectedPrimaryEntityIds,
      tradingPartnerIds: this.selectedTradingPartnerIds,
      mneCurrency: this.currencyOptionId === 1,
      mneId: this.upe.entityId,
      executiveSummaryIntro: this.executiveSummaryIntro,
      executiveSummaryConclusion: this.executiveSummaryConclusion,
      targetLanguage: this.language,
      newReportInstance: !!this.data.isNewVersion,
      internalLocalfileReportId: this.data.report && this.data.report.reportId,
      isoCode: this.firstPrimaryEntity.domicile.isoCode,
      domicileName: this.firstPrimaryEntity.domicile.name,
      domicileId: this.firstPrimaryEntity.domicile.countryId,
      taxYear: this.workingContainer.taxYear,
      shouldDisplayPageNumber: this.shouldDisplayPageNumber,
      isLimitationWording: this.shouldLimitWording,
      isActive: !this.data.isNewVersion
    };

    this.responseErrors = [];
    const errorMessages = this.validateReportPayload(payload);

    if (errorMessages.length) {
      this.responseErrors = errorMessages;
      return;
    }

    this.generatingReport = true;
    this.subscriptions.push(this._tpReportGeneratorService.generateReport(payload).subscribe((response) => {
      const newReportInstanceId = response.data.reportInstanceId;
      const reportID = response.data.reportId;
      this._reportService.updateRepostActiveStatus(reportID, newReportInstanceId, this.workingContainer.containerId).subscribe(()=>{});
      this.generatingReport = false;
      this.generatedReport = true;

      this.success();
      this.exitModal();
    }));
  }

  getCurrencyIsoCode(optionId: number): string {
    let currency = this.taxJurisdictionCurrency && this.taxJurisdictionCurrency.isoCode;
    if (optionId === FinancialDataValueEnteredInEnum.MNE) {
      currency = this.data && this.mneCurrency && this.mneCurrency.isoCode;
    }
    return `${currency}`;
  }

  validateReportPayload(payload: any): string[] {
    const errorMessages = [];

    if (!payload.name) {
      errorMessages.push(TranslationConstants.REPORT_GENERATION_REPORT_TITLE_REQUIRED);
    } else if (payload.name.includes(`"`)) {
      errorMessages.push(TranslationConstants.REPORT_GENERATION_REPORT_DOUBLE_QUOTES);
    }

    if (!payload.primaryEntityIds.length) {
      errorMessages.push(TranslationConstants.REPORT_GENERATION_ERROR_NO_PRIMARY_ENTITY_SELECTED);
    }

    if (!payload.tradingPartnerIds.length) {
      errorMessages.push(TranslationConstants.REPORT_GENERATION_ERROR_NO_TRADING_PARTNER_SELECTED);
    }

    if (!payload.currencyId) {
      errorMessages.push(TranslationConstants.REPORT_GENERATION_ERROR_NO_CURRENCY_SELECTED);
    }

    if (!payload.templateId) {
      errorMessages.push(TranslationConstants.REPORT_GENERATION_ERROR_TEMPLATE_MUST_BE_SELECTED);
    }

    if (payload.selectedTransactionIds.length === 0) {
      errorMessages.push(TranslationConstants.REPORT_GENERATION_ERROR_NO_TRANSACTIONS_SELECTED);
    }

    if (payload.executiveSummaryIntro === '' || payload.executiveSummaryIntro === null) {
      errorMessages.push(TranslationConstants.REPORT_GENERATION_ERROR_EXECUTIVE_SUMMARY_INTRO_MISSING);
    }

    if (payload.executiveSummaryConclusion === '' || payload.executiveSummaryConclusion === null) {
      errorMessages.push(TranslationConstants.REPORT_GENERATION_ERROR_EXECUTIVE_SUMMARY_CONCLUSION_MISSING);
    }

    if (payload.targetLanguage === '' || payload.targetLanguage === null) {
      errorMessages.push(TranslationConstants.REPORT_GENERATION_ERROR_LANGUAGE_MUST_BE_SELECTED);
    }

    return errorMessages;
  }

  getLanguages(jurisdictionId: number, taxYear: number): void {
    this.subscriptions.push(this._compSearchService.getLanguages(jurisdictionId, taxYear).subscribe((res) => {

      this.languages = res.data;
      this.languages.forEach((language) => {
        /* eslint camelcase: ['error', { allow: ['language_iso'] }] */
        language.displayName = this._translateService.instant(`internal_local_file_report.languages.${language.language_iso}`);
      });

      this.languages.sort((language1, language2) => {
        return language1.displayName > language2.displayName ? 1 : -1;
      });
    }));
  }

  pageChanged(event: any): void {
    const currentPage = parseInt(event.page, 10);
    const itemsPerPage = parseInt(event.itemsPerPage, 10);
    const pageStart = (currentPage - 1) * itemsPerPage;
    const pageEnd = (currentPage - 1) * itemsPerPage + itemsPerPage;
    this.viewedTransactions = this.selectableTransactions.slice(pageStart, pageEnd);
  }

  pageSizeChanged(itemsPerPage: string): void {
    const pageSize = parseInt(itemsPerPage, 10);
    this.selectedPageSize = pageSize;
    this.viewedTransactions = this.selectableTransactions.slice(0, pageSize);
  }

  primaryEntitiesChange(): void {
    this.initEntityData();
    this.getPotentialTradingPartners();
    this.tradingPartnersChange();
  }

  filterSelectedTradingPartners(potentialTradingPartnerIdsArr: number[]): void {
    this.selectedTradingPartnerIds = this.selectedTradingPartnerIds.filter(selectedTradingPartnerId =>
      potentialTradingPartnerIdsArr.includes(selectedTradingPartnerId)
    );
  }

  getPotentialTradingPartners(): void {
    let potentialTradingPartnerIds = new Set<number>([]);
    this.selectedPrimaryEntityIds.forEach(selectedPrimaryEntityId => {
      if (this.primaryTradingPartnerMap[selectedPrimaryEntityId] && this.primaryTradingPartnerMap[selectedPrimaryEntityId].length) {
        this.primaryTradingPartnerMap[selectedPrimaryEntityId].forEach(tradingPartnerId => {
          potentialTradingPartnerIds.add(tradingPartnerId);
        });
      }
    });

    const potentialTradingPartnerIdsArr = Array.from(potentialTradingPartnerIds);
    this.potentialTradingPartners = potentialTradingPartnerIdsArr.map(potentialTradingPartnerId => {
      return this.tradingPartnerEntities[potentialTradingPartnerId];
    });

    this.potentialTradingPartners.sort((tradingPartner1, tradingPartner2) => {
      return tradingPartner1.code > tradingPartner2.code ? 1 : -1;
    });

    this.filterSelectedTradingPartners(potentialTradingPartnerIdsArr);
  }

  constructPrimaryTradingTransactionMaps(): void {
    this.data.containerTransactions.forEach(containerTransaction => {
      const sourceEntityId = containerTransaction.legalEntityTransactions[0].entity ? containerTransaction.legalEntityTransactions[0].entity.entityId : null;
      const destEntityId = containerTransaction.legalEntityTransactions[1].entity ? containerTransaction.legalEntityTransactions[1].entity.entityId : null;
      const primaryEntityIds = this.primaryEntityList.map(primaryEntity => primaryEntity.entityId);

      if (primaryEntityIds.includes(sourceEntityId)) {
        this.mapPrimaryTradingPartners(sourceEntityId, destEntityId);
        this.tradingPartnerEntities[destEntityId] = containerTransaction.legalEntityTransactions[1].entity;
        const transactionTag = `${sourceEntityId}-${destEntityId}`;
        this.createPrimaryTradingPartnerTransactionMap(transactionTag, containerTransaction);
      }
      if (primaryEntityIds.includes(destEntityId)) {
        this.mapPrimaryTradingPartners(destEntityId, sourceEntityId);
        this.tradingPartnerEntities[sourceEntityId] = containerTransaction.legalEntityTransactions[0].entity;
        const transactionTag = `${destEntityId}-${sourceEntityId}`;
        this.createPrimaryTradingPartnerTransactionMap(transactionTag, containerTransaction);
      }
    });
  }

  mapPrimaryTradingPartners(primaryEntityId: number, tradingPartnerId: number): void {
    this.primaryTradingPartnerMap[primaryEntityId] = this.primaryTradingPartnerMap[primaryEntityId] || [];
    this.primaryTradingPartnerMap[primaryEntityId].push(tradingPartnerId);
  }

  createPrimaryTradingPartnerTransactionMap(transactionTag: string, containerTransaction: Transaction) {
    this.primaryTradingPartnerTransactionMap[transactionTag] = this.primaryTradingPartnerTransactionMap[transactionTag] || [];
    this.primaryTradingPartnerTransactionMap[transactionTag].push(containerTransaction as SelectableTransaction);
  }

  tradingPartnersChange() {
    if (this.previousSelectedTradingPartnerIds.length > this.selectedTradingPartnerIds.length) {
      const changedTradingPartnerIds = [];
      this.previousSelectedTradingPartnerIds.forEach(previousSelectedTradingPartnerId => {
        if (!this.selectedTradingPartnerIds.includes(previousSelectedTradingPartnerId)) {
          changedTradingPartnerIds.push(previousSelectedTradingPartnerId);
        }
      });
      this.removeSelectedTransactions(changedTradingPartnerIds);
    }
    this.previousSelectedTradingPartnerIds = this.selectedTradingPartnerIds;
    this.selectableTransactions = [];
    this.getPrimaryTradingPairTransactions();

    this.totalItems = this.selectableTransactions.length;
    this.viewedTransactions = this.selectableTransactions.slice(0, this.selectedPageSize);
  }

  removeSelectedTransactions(changedTradingPartnerIds) {
    changedTradingPartnerIds.forEach(tradingPartnerId => {
      const partialTransactionKey = `-${tradingPartnerId}`;
      Object.keys(this.primaryTradingPartnerTransactionMap).forEach(key => {
        if (key.includes(partialTransactionKey)) {
          this.primaryTradingPartnerTransactionMap[key].forEach(transaction => {
            transaction.selected = false;
          });
        }
      });
    });
  }

  getPrimaryTradingPairTransactions(): void {
    this.selectedPrimaryEntityIds.forEach(selectedPrimaryEntityId => {
      this.selectedTradingPartnerIds.forEach(selectedtradingPartner => {
        const transactionTag = `${selectedPrimaryEntityId}-${selectedtradingPartner}`;
        if (this.primaryTradingPartnerTransactionMap[transactionTag]) {
          this.primaryTradingPartnerTransactionMap[transactionTag].forEach(transaction => {
            if (!this.selectableTransactions.includes(transaction)) {
              this.selectableTransactions.push(transaction);
            }
          });
        }
      });
    });
  }

  selectedTransactionChange(transaction: SelectableTransaction): void {
    transaction.selected = !transaction.selected;
  }

  getEntityCode(transaction: Transaction, isSource = true): string {
    return this._utilsService.getEntityCodeFromTransaction(transaction, isSource);
  }

  toggleDisplayPageNumbers(): void {
    this.shouldDisplayPageNumber = !this.shouldDisplayPageNumber;
  }

  toggleLimitationWording(): void {
    this.shouldLimitWording = !this.shouldLimitWording;
  }

  private getTemplatesForEntity(entityId: number): void {
    this.subscriptions.push(this._tpReportGeneratorService.getTemplatesForEntity(entityId).subscribe(({ data }) => {
      this.templates = data.templates;
      const defaultTemplateName = this.workingContainer.taxYear >= 2019 ? this.default2019Template : this.default2018Template;
      const defaultTemplate: ReportGeneratorTemplate = this.templates.find((template) => template.name === defaultTemplateName);
      this.templateId = defaultTemplate && defaultTemplate.templateId;
    }));
  }

  private getMneCurrencyInfo(currencyId: number): void {
    this.subscriptions.push(this._currenciesService.show(currencyId).subscribe((data) => {
      this.mneCurrency = data.data;
    }));
  }

  private getUpe(): void {
    this.subscriptions.push(this._entitiesService.getUpe().subscribe((data) => {
      this.upe = data.data;
      this.getMneCurrencyInfo(this.upe.upeCurrency.currencyId);
    }));
  }

  private getPrimaryEntityCurrency(): void {
    this.subscriptions.push(this._countriesService.show(this.countryId).subscribe(({ data }: ObjectResponse<Country>) => {
      this.taxJurisdictionCurrency = data.defaultCurrency;
    }));
  }
}
